import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  loginApi,
  logoutApi,
  forgotPasswordApi,
  verifyOtpApi,
  resetPasswordApi,
  verifySessionApi,
  type AuthRole,
} from "../../services/authService";

interface AuthState {
  user: string | null;
  role: AuthRole | null;
  // adminId:string | null;
  partnerId: string | null;
  displayName: string | null;
  isAuthenticated: boolean;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  message: string | null;
  error: string | null;
}

const normalizeRole = (role: string): AuthRole =>
  role === "publisher" ? "partner" : (role as AuthRole);

const loadAuthState = (): Partial<AuthState> => {
  try {
    const user = localStorage.getItem('auth_user');
    const rawRole = localStorage.getItem('auth_role');
    const role = (rawRole === "publisher" ? "partner" : rawRole) as AuthRole | null;
    if (user && role) {
      return {
        user,
        role,
        partnerId:
          localStorage.getItem('auth_partner_id')
          || localStorage.getItem('auth_publisher_id'),
        displayName: localStorage.getItem('auth_display_name'),
        isAuthenticated: true,
      };
    }
  } catch { }
  return {};
};

const persistSession = (payload: {
  user: string;
  role: AuthRole;
  partnerId?: string | null;
  name?: string | null;
}) => {
  localStorage.setItem('auth_user', payload.user);
  localStorage.setItem('auth_role', payload.role);
  if (payload.partnerId) {
    localStorage.setItem('auth_partner_id', payload.partnerId);
  } else {
    localStorage.removeItem('auth_partner_id');
  }
  localStorage.removeItem('auth_publisher_id');
  if (payload.name) {
    localStorage.setItem('auth_display_name', payload.name);
  } else {
    localStorage.removeItem('auth_display_name');
  }
};

const clearSession = () => {
  localStorage.removeItem('auth_user');
  localStorage.removeItem('auth_role');
  localStorage.removeItem('auth_partner_id');
  localStorage.removeItem('auth_publisher_id');
  localStorage.removeItem('auth_display_name');
};

const initialState: AuthState = {
  user: null,
  role: null,
  partnerId: null,
  displayName: null,
  isAuthenticated: false,
  status: 'idle',
  message: null,
  error: null,
  ...loadAuthState(),
};

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const data = await loginApi(email, password);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Login failed');
    }
  }
);

export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { rejectWithValue }) => {
    try {
      return await verifySessionApi();
    } catch (err: any) {
      return rejectWithValue(err.message || 'Session invalid');
    }
  }
);

export const sendOtp = createAsyncThunk(
  'auth/sendOtp',
  async (email: string, { rejectWithValue }) => {
    try {
      const data = await forgotPasswordApi(email);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to send OTP');
    }
  }
);

export const verifyOtp = createAsyncThunk(
  'auth/verifyOtp',
  async (payload: { email: string; otp: string }, { rejectWithValue }) => {
    try {
      const data = await verifyOtpApi(payload);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'OTP verification failed');
    }
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async (
    payload: { email: string; newPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const data = await resetPasswordApi(payload);
      return data;
    } catch (err: any) {
      return rejectWithValue(err.message || 'Password reset failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      void logoutApi();
      state.user = null;
      state.role = null;
      state.partnerId = null;
    
      state.displayName = null;
      state.isAuthenticated = false;
      state.status = 'idle';
      state.message = null;
      state.error = null;
      clearSession();
    },
    clearAuthFeedback: (state) => {
      state.message = null;
      state.error = null;
      state.status = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        const role = normalizeRole(String(action.payload.role));
        const partnerId = action.payload.partnerId ?? null;
        state.status = 'succeeded';
        state.user = action.payload.user;
        state.role = role;
        state.partnerId = partnerId;
        state.displayName = action.payload.name ?? null;
        state.isAuthenticated = true;
        state.message = 'Login successful';
        persistSession({
          user: action.payload.user,
          role,
          partnerId,
          name: action.payload.name ?? null,
        });
      })
      .addCase(login.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })

      .addCase(restoreSession.fulfilled, (state, action) => {
        state.user = action.payload.user;
        const role = normalizeRole(String(action.payload.role));
        state.role = role;
        state.partnerId = action.payload.partnerId;
        state.displayName = action.payload.name;
        state.isAuthenticated = true;
        persistSession({
          user: action.payload.user,
          role,
          partnerId: action.payload.partnerId,
          name: action.payload.name,
        });
      })
      .addCase(restoreSession.rejected, (state) => {
        state.user = null;
        state.role = null;
        state.partnerId = null;
        state.displayName = null;
        state.isAuthenticated = false;
        clearSession();
      })

      .addCase(sendOtp.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(sendOtp.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.message = action.payload.message || 'OTP sent to your email';
      })
      .addCase(sendOtp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })

      .addCase(verifyOtp.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.message = action.payload.message || 'OTP verified successfully';
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })

      .addCase(resetPassword.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.message = action.payload.message || 'Password reset successful';
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  },
});

export const { logout, clearAuthFeedback } = authSlice.actions;
export default authSlice.reducer;
